<template>
  <div>
    <!-- ── Settings Sheet ───────────────────────────────────── -->
    <div class="ps-sheet">
      <!-- Loading overlay — shown while placePanels + save is running -->
      <div v-if="saving" class="ps-loading-overlay">
        <span class="ps-loading-spinner"></span>
        <p class="ps-loading-text">{{ savingText }}</p>
      </div>

      <div class="ps-header">
        <span class="ps-title">Settings</span>
        <button class="ps-close" :disabled="saving" @click="$emit('close')">&#x2715;</button>
      </div>

      <div class="ps-body">
        <!-- Panel selector -->
        <div class="ps-row ps-row--full">
          <span class="ps-label">Panel</span>
          <button class="ps-selector" @click="openPanel('panel')">
            <span class="ps-selector-text" :class="{ 'ps-selector-placeholder': !selectedPanelDisplay }">
              {{ selectedPanelDisplay || 'Select a panel…' }}
            </span>
            <span class="ps-selector-arrow">&#9660;</span>
          </button>
        </div>

        <!-- Inverter selector -->
        <div class="ps-row ps-row--full">
          <span class="ps-label">Inverter</span>
          <button class="ps-selector" @click="openPanel('inverter')">
            <span class="ps-selector-text" :class="{ 'ps-selector-placeholder': !selectedInverterDisplay }">
              {{ selectedInverterDisplay || 'Select an inverter…' }}
            </span>
            <span class="ps-selector-arrow">&#9660;</span>
          </button>
        </div>

        <!-- Inverter Quantity -->
        <div class="ps-row ps-row--inline">
          <span class="ps-label">Inverter Quantity</span>
          <div class="ps-stepper">
            <button class="ps-stepper-btn" @click="decrementQty" :disabled="localQty <= 1">&#9661;</button>
            <span class="ps-stepper-val">{{ localQty }}</span>
            <button class="ps-stepper-btn" @click="incrementQty">&#9651;</button>
          </div>
        </div>
      </div>

      <div class="ps-footer">
        <button
          class="ps-save-btn"
          :disabled="saving || !localPanel || !localInverter"
          @click="onSaveAndContinue"
        >
          Save &amp; Continue
        </button>
      </div>
    </div>

    <!-- ── Selection Overlay ────────────────────────────────── -->
    <div class="sel-overlay" v-if="activePanel" @click.self="closePanel">
      <div class="sel-panel">
        <!-- Header -->
        <div class="sel-header">
          <span class="sel-title">{{ activePanel === 'panel' ? 'Module Selection' : 'Inverter Selection' }}</span>
          <span class="sel-current" v-if="activePanel === 'panel' && selectedPanelDisplay">{{ selectedPanelDisplay }}</span>
          <span class="sel-current" v-if="activePanel === 'inverter' && selectedInverterDisplay">{{ selectedInverterDisplay }}</span>
          <span class="sel-chevron">&#9650;</span>
        </div>

        <!-- Search bar -->
        <div class="sel-search-bar">
          <div class="sel-search-group">
            <label class="sel-search-label">{{ activePanel === 'panel' ? 'Make' : 'Brand' }}</label>
            <input
              ref="searchInput"
              class="sel-search-input"
              v-model="searchQuery"
              :placeholder="activePanel === 'panel' ? 'Search Module' : 'Search Inverter'"
              @input="onSearchInput"
              @keyup.enter="triggerSearch"
            />
          </div>
          <div class="sel-search-group sel-search-group--sm">
            <label class="sel-search-label">Size</label>
            <input class="sel-range-input" v-model.number="minWatt" type="number" :placeholder="activePanel === 'panel' ? 'Min(W)' : 'Min(kW)'" @input="onSearchInput" />
          </div>
          <div class="sel-search-group sel-search-group--sm">
            <label class="sel-search-label">&nbsp;</label>
            <input class="sel-range-input" v-model.number="maxWatt" type="number" :placeholder="activePanel === 'panel' ? 'Max(W)' : 'Max(kW)'" @input="onSearchInput" />
          </div>
          <button class="sel-search-btn" @click="triggerSearch" :disabled="listLoading">
            <svg v-if="!listLoading" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <span v-else class="sel-spinner"></span>
          </button>
        </div>

        <!-- List -->
        <div class="sel-list-header">
          {{ listLoading ? 'Loading…' : (listItems.length ? 'Results' : 'No results') }}
        </div>
        <div class="sel-list" ref="listEl">
          <div
            v-for="item in listItems"
            :key="item.id"
            class="sel-item"
            :class="{ 'sel-item--selected': isSelected(item) }"
            @click="selectItem(item)"
          >
            <div class="sel-item-main">
              <span class="sel-item-brand">{{ getItemBrand(item) }}</span>
              <span class="sel-item-model">{{ getItemModel(item) }}</span>
            </div>
            <div class="sel-item-right">
              <span v-if="activePanel === 'panel'" class="sel-item-watt">{{ getPanelWatt(item) }} W</span>
              <span v-else class="sel-item-watt">{{ getInverterPower(item) }}</span>
            </div>
          </div>
          <div v-if="!listLoading && listItems.length === 0" class="sel-empty">
            No {{ activePanel === 'panel' ? 'panels' : 'inverters' }} found. Try a different search.
          </div>
          <!-- Sentinel element — triggers loadMore when it scrolls into view -->
          <div ref="sentinel" class="sel-sentinel"></div>
          <div v-if="listLoadingMore" class="sel-loading-more">Loading more…</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '../../services/api/index';
import { useStudioStore } from '../store/studioStore';

export default {
  name: 'PanelSettings',
  emits: ['save-and-continue', 'close'],

  props: {
    saving: {
      type: Boolean,
      default: false,
    },
    savingText: {
      type: String,
      default: 'Placing panels & saving design…',
    },
  },


  data() {
    const store = useStudioStore();
    return {
      store,
      localQty: store.inverterQuantity ?? 1,
      localPanel: store.selectedPanel || null,
      localInverter: store.selectedInverter || null,

      // Overlay state
      activePanel: null,  // 'panel' | 'inverter' | null
      searchQuery: '',
      minWatt: null,
      maxWatt: null,
      listItems: [],
      listLoading: false,
      listLoadingMore: false,
      nextUrl: null,
      _searchTimer: null,
      _observer: null,
    };
  },

  computed: {
    selectedPanelDisplay() {
      if (!this.localPanel) return '';
      return `${this.localPanel.brand || this.localPanel.characteristics?.manufacturer || ''} ${this.localPanel.model || ''}`.trim();
    },
    selectedInverterDisplay() {
      if (!this.localInverter) return '';
      const brand = this.localInverter.Manufacturer || this.localInverter.brand || '';
      const model = this.localInverter.Make || this.localInverter.model || '';
      return `${brand} ${model}`.trim();
    },
  },

  methods: {
    // ── Field helpers (panels: flat {brand,model,moduleProperties}, inverters: {Manufacturer,Make,Size,max_ac_power_W})
    getItemBrand(item) {
      if (this.activePanel === 'inverter') return item.Manufacturer || item.brand || '';
      return item.brand || item.characteristics?.manufacturer || '';
    },
    getItemModel(item) {
      if (this.activePanel === 'inverter') return item.Make || item.model || '';
      return item.model || '';
    },
    getPanelWatt(panel) {
      // moduleProperties.capacity is in kW — multiply by 1000 for W
      const cap = panel?.moduleProperties?.capacity;
      if (cap != null) return Math.round(cap * 1000);
      // fallback: p_mp_ref is in W directly, or characteristics.p_mp_ref
      const ref = panel?.p_mp_ref ?? panel?.characteristics?.p_mp_ref ?? panel?.capacity;
      return ref ?? '—';
    },
    getInverterPower(inv) {
      // Size field is in kW; fallback to max_ac_power_W / 1000
      const kw = inv?.Size != null ? inv.Size
               : inv?.max_ac_power_W != null ? (inv.max_ac_power_W / 1000)
               : inv?.rated_power ?? inv?.power_rating ?? null;
      if (kw == null) return '—';
      return `${parseFloat(kw).toFixed(2)} kW`;
    },

    // ── Response unwrap helper ─────────────────────────────────
    _unwrap(res) {
      // API returns {success, data: {results, next, previous}} or flat {results, next}
      const outer = res?.data;
      const inner = (outer && 'results' in outer) ? outer
                  : (outer?.data && 'results' in outer.data) ? outer.data
                  : null;
      return {
        results: inner?.results ?? (Array.isArray(outer) ? outer : []),
        next: inner?.next ?? null,
      };
    },

    // ── Overlay open/close ─────────────────────────────────────
    async openPanel(type) {
      this.activePanel = type;
      this.searchQuery = '';
      this.minWatt = null;
      this.maxWatt = null;
      this.listItems = [];
      this.nextUrl = null;
      this._disconnectObserver();
      await this.loadList();
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
        this._connectObserver();
      });
    },
    closePanel() {
      this._disconnectObserver();
      this.activePanel = null;
    },

    // ── List load & search ─────────────────────────────────────
    async loadList(query = {}) {
      this.listLoading = true;
      this.nextUrl = null;
      try {
        let res;
        if (this.activePanel === 'panel') {
          res = await API.MASTER_DATA.SEARCH_MASTER_PANELS(query);
        } else {
          res = await API.MASTER_DATA.SEARCH_MASTER_INVERTERS(query);
        }
        const { results, next } = this._unwrap(res);
        this.listItems = results;
        this.nextUrl = next;
      } catch (err) {
        console.error('[PanelSettings] List load failed:', err);
        this.listItems = [];
      } finally {
        this.listLoading = false;
      }
    },

    // ── Load more (infinite scroll) ────────────────────────────
    async loadMore() {
      if (!this.nextUrl || this.listLoadingMore || this.listLoading) return;
      this.listLoadingMore = true;
      try {
        const res = await API.MASTER_DATA.LOAD_NEXT(this.nextUrl);
        const { results, next } = this._unwrap(res);
        this.listItems = [...this.listItems, ...results];
        this.nextUrl = next;
      } catch (err) {
        console.error('[PanelSettings] Load more failed:', err);
      } finally {
        this.listLoadingMore = false;
      }
    },

    // ── IntersectionObserver for infinite scroll ───────────────
    _connectObserver() {
      const sentinel = this.$refs.sentinel;
      if (!sentinel) return;
      this._observer = new IntersectionObserver(
        (entries) => { if (entries[0].isIntersecting) this.loadMore(); },
        { root: this.$refs.listEl, threshold: 0.1 }
      );
      this._observer.observe(sentinel);
    },
    _disconnectObserver() {
      if (this._observer) { this._observer.disconnect(); this._observer = null; }
    },

    onSearchInput() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.triggerSearch(), 400);
    },

    triggerSearch() {
      clearTimeout(this._searchTimer);
      const query = {};
      // API uses 'query' param for text search (matches manufacturer/brand/model)
      if (this.searchQuery?.trim()) query.query = this.searchQuery.trim();
      // API uses p_min/p_max for wattage (W for panels, kW for inverters)
      if (this.minWatt != null && this.minWatt !== '') query.p_min = this.minWatt;
      if (this.maxWatt != null && this.maxWatt !== '') query.p_max = this.maxWatt;
      this._disconnectObserver();
      this.loadList(query).then(() => {
        this.$nextTick(() => this._connectObserver());
      });
    },

    // ── Selection ──────────────────────────────────────────────
    isSelected(item) {
      if (this.activePanel === 'panel') return this.localPanel?.id === item.id;
      return this.localInverter?.id === item.id;
    },

    selectItem(item) {
      if (this.activePanel === 'panel') {
        this.localPanel = item;
        this.store.SET_SELECTED_PANEL(item);
      } else {
        this.localInverter = item;
        this.store.SET_SELECTED_INVERTER(item);
      }
      this.closePanel();
    },

    // ── Stepper ────────────────────────────────────────────────
    incrementQty() { this.localQty += 1; },
    decrementQty()  { if (this.localQty > 1) this.localQty -= 1; },

    // ── Save ───────────────────────────────────────────────────
    onSaveAndContinue() {
      this.store.SET_INVERTER_QUANTITY(this.localQty);
      this.$emit('save-and-continue', {
        panel: this.localPanel,
        inverter: this.localInverter,
        inverterQuantity: this.localQty,
      });
    },
  },

  beforeUnmount() {
    this._disconnectObserver();
    clearTimeout(this._searchTimer);
  },
};
</script>

<style scoped>
/* ── Sheet ─────────────────────────────────────────────────── */
.ps-sheet {
  position: fixed;
  overflow: hidden;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  background: #fff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 24px rgba(0,0,0,.18);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
}

/* ── Loading overlay ────────────────────────────────────────── */
.ps-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.93);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  border-radius: 16px 16px 0 0;
}
.ps-loading-spinner {
  display: block;
  width: 38px;
  height: 38px;
  border: 3px solid #e4e4e4;
  border-top-color: #3d86f5;
  border-radius: 50%;
  animation: ps-spin 0.75s linear infinite;
}
.ps-loading-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #444;
  letter-spacing: 0.01em;
}
@keyframes ps-spin { to { transform: rotate(360deg); } }

.ps-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.ps-title  { font-size: 16px; font-weight: 600; color: #1a1a1a; }
.ps-close  { background: none; border: none; font-size: 18px; color: #888; cursor: pointer; padding: 2px 6px; }
.ps-body   { padding: 16px 20px; display: flex; flex-direction: column; gap: 16px; }
.ps-row    { display: flex; flex-direction: column; gap: 6px; }
.ps-row--inline { flex-direction: row; align-items: center; justify-content: space-between; }
.ps-label  { font-size: 13px; color: #555; font-weight: 500; }

/* selector button */
.ps-selector {
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; border: 1px solid #d9d9d9; border-radius: 8px;
  background: #fff; cursor: pointer; font-size: 14px; text-align: left;
}
.ps-selector:hover { border-color: #3d86f5; }
.ps-selector-text { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #1a1a1a; }
.ps-selector-placeholder { color: #aaa; }
.ps-selector-arrow { color: #aaa; font-size: 10px; margin-left: 8px; }

/* stepper */
.ps-stepper {
  display: flex; align-items: center; border: 1px solid #d9d9d9;
  border-radius: 8px; overflow: hidden; height: 36px;
}
.ps-stepper-btn {
  background: #fafafa; border: none; cursor: pointer; width: 34px; height: 100%;
  font-size: 11px; color: #555; display: flex; align-items: center; justify-content: center;
}
.ps-stepper-btn:disabled { color: #ccc; cursor: default; }
.ps-stepper-btn:first-child { border-right: 1px solid #d9d9d9; }
.ps-stepper-btn:last-child  { border-left:  1px solid #d9d9d9; }
.ps-stepper-val { width: 40px; text-align: center; font-size: 14px; font-weight: 600; color: #1a1a1a; }

/* footer */
.ps-footer { padding: 12px 20px 24px; }
.ps-save-btn {
  width: 100%; padding: 14px; background: #3d86f5; color: #fff; border: none;
  border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer; transition: background .2s;
}
.ps-save-btn:hover:not(:disabled) { background: #2970d8; }
.ps-save-btn:disabled { background: #b0c9f7; cursor: not-allowed; }

/* ── Overlay ────────────────────────────────────────────────── */
.sel-overlay {
  position: fixed; inset: 0; z-index: 1100;
  background: rgba(0,0,0,.55); display: flex; align-items: flex-end;
}
.sel-panel {
  width: 100%; max-height: 75vh; background: #2a2a2a;
  border-radius: 16px 16px 0 0; display: flex; flex-direction: column; overflow: hidden;
}

/* overlay header */
.sel-header {
  display: flex; align-items: center; gap: 8px;
  padding: 16px 18px 10px; border-bottom: 1px solid #3a3a3a;
}
.sel-title   { font-size: 15px; font-weight: 700; color: #fff; flex: 1; }
.sel-current { font-size: 13px; color: #4da6ff; max-width: 45%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sel-chevron { color: #4da6ff; font-size: 11px; }

/* search bar */
.sel-search-bar {
  display: flex; align-items: flex-end; gap: 8px;
  padding: 12px 14px; background: #2a2a2a; border-bottom: 1px solid #3a3a3a; flex-wrap: nowrap;
}
.sel-search-group { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
.sel-search-group--sm { flex: 0 0 76px; }
.sel-search-label { font-size: 11px; color: #999; }
.sel-search-input, .sel-range-input {
  background: #3a3a3a; border: 1px solid #4a4a4a; border-radius: 6px;
  color: #fff; padding: 8px 10px; font-size: 13px; width: 100%; box-sizing: border-box; outline: none;
}
.sel-range-input { -moz-appearance: textfield; }
.sel-range-input::-webkit-inner-spin-button,
.sel-range-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.sel-search-input::placeholder, .sel-range-input::placeholder { color: #666; }
.sel-search-btn {
  flex-shrink: 0; width: 38px; height: 38px; background: #3d86f5; border: none; border-radius: 6px;
  color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center;
  align-self: flex-end;
}
.sel-search-btn:disabled { background: #2a5090; cursor: not-allowed; }

/* list */
.sel-list-header {
  padding: 10px 16px 6px; font-size: 12px; font-weight: 600;
  color: #888; text-transform: uppercase; letter-spacing: .4px; background: #2a2a2a;
}
.sel-list {
  flex: 1; overflow-y: auto; padding: 0 0 8px;
}
.sel-list::-webkit-scrollbar { width: 4px; }
.sel-list::-webkit-scrollbar-thumb { background: #444; border-radius: 4px; }

.sel-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-bottom: 1px solid #333; cursor: pointer; transition: background .15s;
}
.sel-item:hover { background: #333; }
.sel-item--selected { background: #1a3a5c; }
.sel-item--selected:hover { background: #1e4470; }

.sel-item-main { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.sel-item-brand {
  font-size: 13px; font-weight: 600; color: #fff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sel-item-model { font-size: 12px; color: #aaa; }
.sel-item-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; margin-left: 12px; }
.sel-item-watt  { font-size: 13px; font-weight: 600; color: #e0e0e0; white-space: nowrap; }

.sel-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.sel-empty { padding: 24px 16px; text-align: center; color: #666; font-size: 13px; }
.sel-sentinel { height: 1px; }
.sel-loading-more { padding: 10px 16px; text-align: center; color: #888; font-size: 12px; }
</style>
